import { useEffect, useMemo, useState } from 'react';
import type { AlertConfigResponse, DndConfig } from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 2 — Do Not Disturb 윈도우.
// appliesTo 는 INFO / WARN 만 허용. FATAL / ERROR 는 DND 무시하고 항상 발송.

type ApplyTo = 'INFO' | 'WARN';
const APPLY_OPTIONS: ApplyTo[] = ['INFO', 'WARN'];

function equalDnd(a: DndConfig, b: DndConfig): boolean {
  if (a.enabled !== b.enabled) return false;
  if (a.startHhmm !== b.startHhmm) return false;
  if (a.endHhmm !== b.endHhmm) return false;
  if (a.appliesTo.length !== b.appliesTo.length) return false;
  for (const v of a.appliesTo) if (!b.appliesTo.includes(v)) return false;
  return true;
}

interface Props {
  config: AlertConfigResponse;
  onSave: (dnd: DndConfig) => void;
  isSaving: boolean;
}

export function DNDSection({ config, onSave, isSaving }: Props): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DndConfig>(config.dnd);

  useEffect(() => {
    if (!editing) setDraft(config.dnd);
  }, [config.dnd, editing]);

  const dirty = useMemo(() => !equalDnd(draft, config.dnd), [draft, config.dnd]);

  const toggleApply = (v: ApplyTo): void => {
    setDraft((prev) => {
      const next = prev.appliesTo.includes(v)
        ? prev.appliesTo.filter((x) => x !== v)
        : [...prev.appliesTo, v];
      return { ...prev, appliesTo: next };
    });
  };

  const startEdit = (): void => {
    setDraft(config.dnd);
    setEditing(true);
  };
  const cancel = (): void => {
    setDraft(config.dnd);
    setEditing(false);
  };
  const save = (): void => {
    onSave(draft);
    setEditing(false);
  };

  return (
    <section className="as-card" data-testid="as-dnd-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">2. DND (Do Not Disturb)</h3>
          <div className="as-section__sub">
            지정된 시간대에는 선택된 심각도 이벤트의 알림을 억제합니다. FATAL /
            ERROR 는 본 정책과 무관하게 항상 발송됩니다.
          </div>
        </div>
        <div className="as-section__actions">
          {dirty && <span className="as-dirty">저장되지 않음</span>}
          {!editing ? (
            <button type="button" className="as-btn" onClick={startEdit}>
              편집
            </button>
          ) : (
            <>
              <button
                type="button"
                className="as-btn as-btn--ghost"
                onClick={cancel}
                disabled={isSaving}
              >
                취소
              </button>
              <button
                type="button"
                className="as-btn as-btn--primary"
                onClick={save}
                disabled={!dirty || isSaving}
              >
                저장
              </button>
            </>
          )}
        </div>
      </div>

      <div className="as-row">
        <label className="as-chip" data-testid="as-dnd-enabled">
          <input
            type="checkbox"
            checked={draft.enabled}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, enabled: e.target.checked }))
            }
          />
          <span>활성화</span>
        </label>

        <div className="as-field">
          <label className="as-field__label" htmlFor="as-dnd-start">
            시작 (HH:MM)
          </label>
          <input
            id="as-dnd-start"
            type="time"
            className="as-input"
            value={draft.startHhmm}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, startHhmm: e.target.value }))
            }
          />
        </div>

        <div className="as-field">
          <label className="as-field__label" htmlFor="as-dnd-end">
            종료 (HH:MM)
          </label>
          <input
            id="as-dnd-end"
            type="time"
            className="as-input"
            value={draft.endHhmm}
            disabled={!editing || isSaving}
            onChange={(e) =>
              setDraft((p) => ({ ...p, endHhmm: e.target.value }))
            }
          />
        </div>
      </div>

      <div>
        <div className="as-field__label" style={{ marginBottom: 4 }}>
          적용 대상 severity
        </div>
        <div className="as-chip-row">
          {APPLY_OPTIONS.map((v) => {
            const active = draft.appliesTo.includes(v);
            return (
              <label
                key={v}
                className={`as-chip ${active ? 'as-chip--active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  disabled={!editing || isSaving}
                  onChange={() => toggleApply(v)}
                />
                <span>{v}</span>
              </label>
            );
          })}
        </div>
      </div>
    </section>
  );
}
